<template>
  <div v-if="hasMultipleYears" class="time-slider" ref="wrapper">
    <!-- Minimized -->
    <q-btn 
      v-if="!expanded" 
      @click="expanded = true, $emit('panel-opened')"
      :color="$q.dark.isActive ? 'dark' : 'white'"
      :text-color="$q.dark.isActive ? 'grey-5' : 'grey-8'"
      size="md"
      class="time-slider-btn"
      no-caps
    >
      <q-icon name="event" size="14px" class="q-mr-xs" />
      {{ currentYear }}
      <q-icon name="keyboard_arrow_up" size="14px" class="q-ml-xs" />
    </q-btn>

    <!-- Expanded -->
    <q-card v-else class="time-slider-expanded q-pa-md" @click.stop>
      <div class="row items-center q-gutter-sm">
        <span class="year-label">{{ minYear }}</span>
        
        <div class="slider-container col">
          <q-slider
            v-model="currentYear"
            :min="minYear"
            :max="maxYear"
            :step="1"
            :color="$q.dark.isActive ? 'secondary' : 'primary'"
            :markers="availableYears.length <= 8"
             :label-value="currentYear"
             label
            @update:model-value="onYearChange"
          />
        </div>
        
        <span class="year-label">{{ maxYear }}</span>
      </div>
    </q-card>
  </div>
</template>

<script>
export default {
  name: 'TimeSlider',
  
  props: {
    availableYears: { type: Array, default: () => [] },
    modelValue: { type: [Number, String], default: null },
    activePanel: { type: String, default: null }
  },

  emits: ['update:modelValue', 'year-changed', 'panel-opened'],

  data() {
    return {
      expanded: false,
      currentYear: this.modelValue
    }
  },

  computed: {
    hasMultipleYears() {
      return this.availableYears.length > 1
    },
    minYear() {
      return this.availableYears.length ? Math.min(...this.availableYears) : null
    },
    maxYear() {
      return this.availableYears.length ? Math.max(...this.availableYears) : null
    }
  },

  watch: {
    activePanel(val) {
      if (val && val !== 'time') {
         this.expanded = false
      }
    },
    modelValue(val) {
      if (val !== this.currentYear) this.currentYear = val
    },
    availableYears: {
      handler(years) {
        if (!this.currentYear && years.length) {
          this.currentYear = Math.max(...years)
          this.$emit('update:modelValue', this.currentYear)
        }
        this.expanded = false
      },
      immediate: true
    },
    expanded(val) {
      val 
        ? setTimeout(() => document.addEventListener('click', this.onClickOutside), 50)
        : document.removeEventListener('click', this.onClickOutside)
    }
  },

  mounted() {
    document.addEventListener('keydown', this.onKeydown)
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown)
    document.removeEventListener('click', this.onClickOutside)
  },

  methods: {
    onYearChange(year) {
      this.currentYear = year
      this.$emit('update:modelValue', year)
      this.$emit('year-changed', year)
    },
    onClickOutside(e) {
      if (!this.$refs.wrapper?.contains(e.target)) this.expanded = false
    },
    onKeydown(e) {
      if (e.key === 'Escape') this.expanded = false
    }
  }
}
</script>

<style lang="scss" scoped>
.time-slider {
  position: absolute;
  bottom: var(--map-ui-bottom);
  left: 50%;
  transform: translateX(-50%);
  z-index: var(--z-map-ui);
}

.time-slider-btn {
  border-radius: 1.25rem;
  box-shadow: var(--shadow-md);
  min-width: 4.5rem;
  padding: 0.25rem 0.75rem;

  body.body--dark & {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
  }
}

.time-slider-expanded {
  min-width: 15rem;
  max-width: 18rem;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);

  body.body--dark & {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
  }
}

.year-label {
  font-size: 0.75rem;
  color: $grey-6;

  body.body--dark & {
    color: $grey-5;
  }
}

.slider-container {
  position: relative;
}

@media (max-width: 48rem), (max-height: 48rem)  {
  .time-slider {
    bottom: var(--map-ui-bottom-mobile);
  }
  .time-slider-expanded {
    min-width: 15rem;
    max-width: 90vw;
  }
}
</style>